import { useMemo } from 'react';

import orderBy from 'lodash/orderBy';

import {
  ExternalLink,
  Skeleton,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';

import { useListEdgeConfigurationsQuery } from 'hooks/queries/omni';

import type { EnhancedEdgeLocation } from '../../types';
import { makeEdgeConfigurationLink } from 'features/cluster/autoscaler/edge-configurations';

type EdgeLocationConfigurationsSectionProps = {
  edgeLocation: EnhancedEdgeLocation;
  clusterId: string;
};

export const EdgeLocationConfigurationsSection = ({
  edgeLocation,
  clusterId,
}: EdgeLocationConfigurationsSectionProps) => {
  const { edgeConfigurations, isLoading } = useListEdgeConfigurationsQuery({
    clusterId,
    edgeLocationId: edgeLocation.id,
  });

  const configurations = useMemo(
    () => orderBy(edgeConfigurations, ['default'], ['desc']),
    [edgeConfigurations]
  );
  const hasConfigurations = configurations.length > 0;

  if (isLoading) {
    return (
      <Stack gap={16} data-testid="edge-location-configurations-section">
        <Typography variant="L12B">Configuration</Typography>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Typography
            variant="L12R"
            color={tokens.theme.fg.structural.secondary}
          >
            Edge configuration
          </Typography>
          <Skeleton
            width={180}
            height={22}
            data-testid="edge-location-configurations-skeleton"
          />
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack gap={16} data-testid="edge-location-configurations-section">
      <Typography variant="L12B">Configuration</Typography>
      {hasConfigurations ? (
        <Stack
          gap={8}
          data-testid="edge-location-configurations-list"
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Typography
            variant="L12R"
            color={tokens.theme.fg.structural.secondary}
          >
            Edge configuration
          </Typography>
          <Stack gap={8} sx={{ minWidth: 0 }}>
            {configurations.map((configuration) => (
              <ExternalLink
                key={configuration.id}
                link={makeEdgeConfigurationLink(clusterId)}
                data-testid={`edge-location-configuration-link-${configuration.id}`}
                sx={{ typography: 'P14R', minWidth: 0 }}
              >
                {configuration.name}
              </ExternalLink>
            ))}
          </Stack>
        </Stack>
      ) : (
        <Typography
          variant="P14R"
          color="text.secondary"
          data-testid="edge-location-configurations-empty"
        >
          No edge configurations yet.
        </Typography>
      )}
    </Stack>
  );
};
